/**
 * Extreme Weather Service
 *
 * Aggregates scientifically sourced disaster and hazard data from:
 *   - NASA EONET v3  (wildfires, storms, floods, volcanoes, landslides, etc.)
 *   - USGS Earthquake Catalog (M4.5+, geo-filtered)
 *   - GDACS (UN-backed multi-hazard alert system, via CORS proxy in dev)
 *
 * YouTube-based "awareness" metrics have been removed entirely because
 * video counts do not constitute a scientifically valid severity signal.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

const EONET_API = "https://eonet.gsfc.nasa.gov/api/v3";
const USGS_API = "https://earthquake.usgs.gov/fdsnws/event/1/query";
const GDACS_API = "https://www.gdacs.org/gdacsapi/api/events/geteventlist/MAP";
const GDACS_EVENT_TYPES = "TC,FL,DR,EQ,VO,WF,LS";
/** Multiple CORS proxy fallbacks for better global data reliability */
const CORS_PROXIES = [
  "https://corsproxy.io/?",
  "https://api.allorigins.win/raw?url=",
  "https://cors-anywhere.herokuapp.com/",
];

// ─── Types ───────────────────────────────────────────────────────────────────

export type EventSource = "EONET" | "USGS" | "GDACS";
export type EventStatus = "open" | "closed";

/** A normalised natural hazard / disaster event across all three sources. */
export interface NaturalEvent {
  id: string;
  title: string;
  /** Machine-readable category id (e.g. 'wildfires', 'earthquakes') */
  category: string;
  /** Human-readable category label (e.g. 'Wildfire', 'Earthquake') */
  categoryLabel: string;
  status: EventStatus;
  /** ISO-8601 date string of event start / occurrence */
  date: string;
  closedDate?: string;
  /** Numeric magnitude (e.g. Richter scale, hectares burned) */
  magnitude?: number;
  magnitudeUnit?: string;
  /** [lng, lat] */
  coordinates?: [number, number];
  /** Link to the event page on the originating platform */
  link: string;
  source: EventSource;
  /** GDACS alert level: Red | Orange | Yellow | Green */
  alertLevel?: string;
}

/** Per-year event counts broken down by hazard category. */
export interface YearlyHazardStats {
  year: number;
  Wildfire: number;
  Flood: number;
  Storm: number;
  Earthquake: number;
  Volcano: number;
  Drought: number;
  Landslide: number;
  Other: number;
}

/** Aggregate summary object consumed by the UI. */
export interface ExtremeWeatherSummary {
  /** Number of currently OPEN events from EONET */
  activeEvents: number;
  /** Total events recorded in the past 12 months across all sources */
  totalPast12Months: number;
  /** Highest GDACS alert level encountered in the dataset */
  highestAlert: "Red" | "Orange" | "Yellow" | "Green" | null;
  /** Map of categoryLabel → total event count (all years, deduplicated) */
  eventsByCategory: Record<string, number>;
  /** 5-year yearly breakdown by hazard type for the grouped bar chart */
  yearlyStats: YearlyHazardStats[];
  /** Most recent 20 events, sorted newest first */
  recentEvents: NaturalEvent[];
  /** ISO timestamp of when this summary was assembled */
  generatedAt: string;
}

// ─── Category maps ───────────────────────────────────────────────────────────

const EONET_CATEGORY_MAP: Record<string, string> = {
  wildfires: "Wildfire",
  severeStorms: "Storm",
  floods: "Flood",
  volcanoes: "Volcano",
  drought: "Drought",
  earthquakes: "Earthquake",
  landslides: "Landslide",
  seaLakeIce: "Sea/Lake Ice",
  snow: "Snow",
  dustHaze: "Dust/Haze",
  tempExtremes: "Temperature Extreme",
  waterColor: "Water Color",
};

const GDACS_TYPE_MAP: Record<string, string> = {
  TC: "Storm",
  FL: "Flood",
  DR: "Drought",
  EQ: "Earthquake",
  VO: "Volcano",
  WF: "Wildfire",
  LS: "Landslide",
};

const isUserFacingUrl = (url?: string): boolean => {
  if (!url) return false;
  const normalized = url.trim().toLowerCase();
  if (
    normalized.endsWith(".tcw") ||
    normalized.endsWith(".kml") ||
    normalized.endsWith(".kmz")
  ) {
    return false;
  }
  return true;
};

const ALERT_PRIORITY: Record<string, number> = {
  Red: 3,
  Orange: 2,
  Yellow: 1,
  Green: 0,
};

// ─── Fetchers ────────────────────────────────────────────────────────────────

/**
 * Try multiple CORS proxies to fetch URL, returning first successful response
 */
const fetchWithCORSFallback = async (url: string): Promise<Response> => {
  let lastError: Error | null = null;

  for (const proxy of CORS_PROXIES) {
    try {
      const response = await fetch(proxy + encodeURIComponent(url));
      const contentType = response.headers.get("content-type") ?? "";
      if (response.ok && contentType.includes("application/json")) {
        return response;
      }
      lastError = new Error(
        `Proxy ${proxy} returned ${response.status} (${contentType})`,
      );
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      console.warn(`[CORS] Proxy ${proxy} failed:`, lastError.message);
    }
  }

  throw lastError || new Error("All CORS proxies failed");
};

/**
 * Fetch events from NASA EONET filtered by a bounding box around the given
 * coordinates.  Uses a ±10° box to catch regional events.
 *
 * @param lat  Centre latitude
 * @param lng  Centre longitude
 * @param days Number of days to look back (default: 5 years)
 */
export const fetchEONETEvents = async (
  lat: number,
  lng: number,
  days = 365 * 5,
): Promise<NaturalEvent[]> => {
  try {
    const bbox = `${lng - 10},${lat - 10},${lng + 10},${lat + 10}`;
    const url = `${EONET_API}/events?days=${days}&status=all&limit=500&bbox=${bbox}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`EONET responded ${res.status}`);
    const data = await res.json();

    return (data.events ?? []).map((event: any): NaturalEvent => {
      const category = event.categories?.[0];
      // Use the first geometry entry for the primary date/coords
      const geometry = event.geometry?.[0];
      const coords: [number, number] | undefined = geometry?.coordinates
        ? [geometry.coordinates[0], geometry.coordinates[1]]
        : undefined;
      const sourceUrl = event.sources?.[0]?.url;

      return {
        id: event.id,
        title: event.title,
        category: category?.id ?? "other",
        categoryLabel:
          EONET_CATEGORY_MAP[category?.id] ?? category?.title ?? "Other",
        status: event.closed ? "closed" : "open",
        date: geometry?.date ?? "",
        closedDate: event.closed ?? undefined,
        magnitude: geometry?.magnitudeValue ?? undefined,
        magnitudeUnit: geometry?.magnitudeUnit ?? undefined,
        coordinates: coords,
        link: isUserFacingUrl(sourceUrl)
          ? sourceUrl
          : `https://eonet.gsfc.nasa.gov/api/v3/events/${event.id}`,
        source: "EONET",
      };
    });
  } catch (err) {
    console.error("[ExtremeWeather] EONET fetch failed:", err);
    return [];
  }
};

/**
 * Fetch earthquakes M4.5+ from the USGS Earthquake Catalog filtered to a
 * bounding box around the given coordinates.
 *
 * @param lat  Centre latitude
 * @param lng  Centre longitude
 * @param days Number of days to look back (default: 5 years)
 */
export const fetchUSGSEarthquakes = async (
  lat: number,
  lng: number,
  days = 365 * 5,
): Promise<NaturalEvent[]> => {
  try {
    const endTime = new Date().toISOString().split("T")[0];
    const startTime = new Date(Date.now() - days * 86_400_000)
      .toISOString()
      .split("T")[0];

    const params = new URLSearchParams({
      format: "geojson",
      starttime: startTime,
      endtime: endTime,
      minlatitude: String(lat - 10),
      maxlatitude: String(lat + 10),
      minlongitude: String(lng - 10),
      maxlongitude: String(lng + 10),
      minmagnitude: "4.5",
      orderby: "time",
      limit: "500",
    });

    const res = await fetch(`${USGS_API}?${params}`);
    if (!res.ok) throw new Error(`USGS responded ${res.status}`);
    const data = await res.json();

    return (data.features ?? []).map((feature: any): NaturalEvent => {
      const props = feature.properties;
      const coords = feature.geometry?.coordinates;
      return {
        id: feature.id,
        title: props.title,
        category: "earthquakes",
        categoryLabel: "Earthquake",
        status: "closed",
        date: new Date(props.time).toISOString(),
        magnitude: props.mag,
        magnitudeUnit: "M",
        coordinates: coords ? [coords[0], coords[1]] : undefined,
        link: props.url,
        source: "USGS",
      };
    });
  } catch (err) {
    console.error("[ExtremeWeather] USGS fetch failed:", err);
    return [];
  }
};

/**
 * Fetch multi-hazard alerts from GDACS (via CORS proxy) for the past 5 years,
 * filtered to a bounding box around the given coordinates.
 *
 * Covers: TC (tropical cyclone), FL (flood), DR (drought), EQ (earthquake),
 *         VO (volcano), WF (wildfire), LS (landslide).
 */
export const fetchGDACSEvents = async (
  lat: number,
  lng: number,
): Promise<NaturalEvent[]> => {
  try {
    const params = new URLSearchParams({
      eventtypes: GDACS_EVENT_TYPES,
      minlat: String(lat - 10),
      maxlat: String(lat + 10),
      minlon: String(lng - 10),
      maxlon: String(lng + 10),
    });

    const apiUrl = `${GDACS_API}?${params}`;
    const res = await fetchWithCORSFallback(apiUrl);
    if (!res.ok) throw new Error(`GDACS responded ${res.status}`);
    const json = await res.json();
    const items: any[] = json.results ?? json.features ?? [];

    return items.map((item: any): NaturalEvent => {
      const p = item.properties ?? item;
      const typecode: string = p.eventtype ?? "";
      return {
        id: String(p.eventid ?? p.id ?? Math.random()),
        title: p.eventname ?? p.name ?? `${typecode} Event`,
        category: typecode.toLowerCase(),
        categoryLabel: GDACS_TYPE_MAP[typecode] ?? typecode ?? "Other",
        status: "closed",
        date: p.fromdate ?? p.begindate ?? "",
        coordinates:
          p.latitude != null
            ? [Number(p.longitude), Number(p.latitude)]
            : undefined,
        link: p.link ?? "https://www.gdacs.org",
        source: "GDACS",
        alertLevel: p.alertlevel ?? undefined,
      };
    });
  } catch (err) {
    console.error("[ExtremeWeather] GDACS fetch failed:", err);
    return [];
  }
};

// ─── Aggregator ──────────────────────────────────────────────────────────────

/**
 * Deduplicate events across sources.  EONET and GDACS can both carry the same
 * cyclone; we keep the GDACS record because it has an alert level, discarding
 * the EONET duplicate if the titles are sufficiently similar and dates overlap.
 *
 * Simple heuristic: same category + same year + same coordinates within 2°.
 */
const deduplicate = (events: NaturalEvent[]): NaturalEvent[] => {
  const seen = new Set<string>();
  return events.filter((ev) => {
    const year = new Date(ev.date).getFullYear();
    const lngBucket = ev.coordinates ? Math.round(ev.coordinates[0] / 2) : "X";
    const latBucket = ev.coordinates ? Math.round(ev.coordinates[1] / 2) : "X";
    const key = `${ev.categoryLabel}:${year}:${lngBucket}:${latBucket}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Fetch all extreme weather events for a given location from EONET, USGS, and
 * GDACS in parallel, deduplicate, and return a structured summary object ready
 * for rendering.
 */
export const fetchExtremeWeatherSummary = async (
  lat: number,
  lng: number,
): Promise<ExtremeWeatherSummary> => {
  // Fire all three requests in parallel — independent data sources
  const [eonetEvents, usgsEvents, gdacsEvents] = await Promise.all([
    fetchEONETEvents(lat, lng),
    fetchUSGSEarthquakes(lat, lng),
    fetchGDACSEvents(lat, lng),
  ]);

  // Merge: EONET (most comprehensive), USGS (authoritative for seismic),
  // then GDACS (adds alert levels for major events)
  const allEvents = deduplicate([
    ...gdacsEvents,
    ...eonetEvents,
    ...usgsEvents,
  ]);

  const now = Date.now();
  const ms12mo = 365 * 24 * 60 * 60 * 1000;
  const currentYear = new Date().getFullYear();

  // Active events (only EONET marks events as "open")
  const activeEvents = allEvents.filter(
    (e) => e.source === "EONET" && e.status === "open",
  ).length;

  // Events in past 12 months
  const totalPast12Months = allEvents.filter((e) => {
    const t = new Date(e.date).getTime();
    return !isNaN(t) && now - t <= ms12mo;
  }).length;

  // Highest GDACS alert level
  let highestAlert: ExtremeWeatherSummary["highestAlert"] = null;
  allEvents.forEach((e) => {
    if (e.alertLevel) {
      const lvl = e.alertLevel as keyof typeof ALERT_PRIORITY;
      if (
        highestAlert === null ||
        (ALERT_PRIORITY[lvl] ?? -1) > (ALERT_PRIORITY[highestAlert] ?? -1)
      ) {
        highestAlert = lvl as ExtremeWeatherSummary["highestAlert"];
      }
    }
  });

  // Events by category (total across all years)
  const eventsByCategory: Record<string, number> = {};
  allEvents.forEach((e) => {
    eventsByCategory[e.categoryLabel] =
      (eventsByCategory[e.categoryLabel] ?? 0) + 1;
  });

  // 5-year yearly stats
  const yearlyStatsMap: Record<number, YearlyHazardStats> = {};
  for (let i = 4; i >= 0; i--) {
    const y = currentYear - i;
    yearlyStatsMap[y] = {
      year: y,
      Wildfire: 0,
      Flood: 0,
      Storm: 0,
      Earthquake: 0,
      Volcano: 0,
      Drought: 0,
      Landslide: 0,
      Other: 0,
    };
  }

  const TRACKED: (keyof Omit<YearlyHazardStats, "year">)[] = [
    "Wildfire",
    "Flood",
    "Storm",
    "Earthquake",
    "Volcano",
    "Drought",
    "Landslide",
  ];

  allEvents.forEach((e) => {
    const year = new Date(e.date).getFullYear();
    if (!yearlyStatsMap[year]) return;
    const cat = e.categoryLabel as keyof Omit<YearlyHazardStats, "year">;
    if (TRACKED.includes(cat)) {
      yearlyStatsMap[year][cat]++;
    } else {
      yearlyStatsMap[year].Other++;
    }
  });

  const yearlyStats = Object.values(yearlyStatsMap).sort(
    (a, b) => a.year - b.year,
  );

  // Recent events (newest first, top 20)
  const recentEvents = [...allEvents]
    .filter((e) => e.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 20);

  return {
    activeEvents,
    totalPast12Months,
    highestAlert,
    eventsByCategory,
    yearlyStats,
    recentEvents,
    generatedAt: new Date().toISOString(),
  };
};

// ─── Global Dashboard Tracker ─────────────────────────────────────────────────

/**
 * Aggregate summary for the homepage Global Disaster Tracker card.
 * Unlike `ExtremeWeatherSummary` (region-scoped), this has no geographic
 * filter — it covers currently active events and recent alerts worldwide.
 */
export interface GlobalHazardSummary {
  /** Currently OPEN events from NASA EONET (status=open, last 90 days) */
  activeEvents: NaturalEvent[];
  /** Globally diverse events: severity-first, per-category capped, geo-balanced (up to 10) */
  recentEvents: NaturalEvent[];
  activeCount: number;
  highestAlert: "Red" | "Orange" | "Yellow" | "Green" | null;
  /** ISO timestamp of when this summary was assembled */
  fetchedAt: string;
  /** True if at least one live API call succeeded; false means all APIs failed */
  isLive: boolean;
}

/**
 * Rough continent zone from longitude, used for geographic diversity.
 * Enhanced zones for better global representation.
 */
const SOURCE_WEIGHTS: Record<EventSource, number> = {
  GDACS: 1.5,
  EONET: 1.0,
  USGS: 0.2,
};

const MAX_EVENTS_PER_ZONE = 5;
const MAX_EVENTS_PER_TYPE: Record<string, number> = {
  Earthquake: 4,
  Flood: 4,
  Wildfire: 4,
  Storm: 4,
  Drought: 4,
  Volcano: 4,
  Landslide: 3,
  Other: 3,
};

const MAX_USA_EVENTS = 6;

function longitudeZone(
  lng: number,
): "americas" | "europe_africa" | "asia_pacific" | "oceania" {
  if (lng < -30) return "americas"; // Americas: < -30°
  if (lng < 50) return "europe_africa"; // Europe/Africa: -30° to 50°
  if (lng < 140) return "asia_pacific"; // Asia: 50° to 140°
  return "oceania"; // Pacific/Oceania: > 140°
}

function isUSCoordinates(lat: number, lng: number): boolean {
  return lng >= -125 && lng <= -66 && lat >= 24 && lat <= 50;
}

/**
 * Enhanced global diversity selector with stronger bias towards international events.
 *
 * Implements a 4-tier selection strategy:
 * 1. Severity-first: Red/Orange GDACS alerts get priority regardless of location
 * 2. Geographic diversity: Ensures representation from all major world regions
 * 3. Category balance: Prevents any single disaster type from dominating
 * 4. Anti-bias filtering: Actively reduces over-representation from any single country
 */
function buildGlobalDiverseEvents(
  events: NaturalEvent[],
  maxResults = 12,
  maxPerCategory = 4,
  maxPerZone = 4,
): NaturalEvent[] {
  const severityScore = (e: NaturalEvent): number => {
    let base = 0;
    if (e.alertLevel === "Red") base = 20;
    else if (e.alertLevel === "Orange") base = 15;
    else if (e.alertLevel === "Yellow") base = 10;
    else if (e.status === "open") base = 8;
    else if (e.source === "USGS" && e.magnitude && e.magnitude >= 6.0) base = 6;

    // Weight by source to reduce USGS dominance.
    const weight = SOURCE_WEIGHTS[e.source] ?? 1;
    return base * weight;
  };

  const scored = [...events]
    .filter((e) => e.date && e.coordinates)
    .map((e) => ({
      event: e,
      score:
        severityScore(e) *
        (isUSCoordinates(e.coordinates![1], e.coordinates![0]) ? 0.75 : 1),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (
        new Date(b.event.date).getTime() - new Date(a.event.date).getTime()
      );
    });

  const eventTypeCounts: Record<string, number> = {};
  const zoneCounts: Record<string, number> = {};
  const usaCount = { value: 0 };
  const used = new Set<string>();
  const result: NaturalEvent[] = [];

  const zones = [
    "americas",
    "europe_africa",
    "asia_pacific",
    "oceania",
  ] as const;

  const getTypeLimit = (category: string) =>
    MAX_EVENTS_PER_TYPE[category] ?? MAX_EVENTS_PER_TYPE.Other;

  // Phase 1: Add high-severity events with source bias and zone caps.
  for (const item of scored) {
    if (result.length >= maxResults) break;
    const event = item.event;
    const zone = longitudeZone(event.coordinates![0]);
    const category = event.categoryLabel;

    if (used.has(event.id)) continue;
    if ((zoneCounts[zone] ?? 0) >= Math.min(maxPerZone, MAX_EVENTS_PER_ZONE))
      continue;
    if ((eventTypeCounts[category] ?? 0) >= getTypeLimit(category)) continue;

    if (isUSCoordinates(event.coordinates![1], event.coordinates![0])) {
      if (usaCount.value >= MAX_USA_EVENTS) continue;
    }

    if (
      event.alertLevel === "Red" ||
      event.alertLevel === "Orange" ||
      item.score > 5
    ) {
      result.push(event);
      used.add(event.id);
      zoneCounts[zone] = (zoneCounts[zone] ?? 0) + 1;
      eventTypeCounts[category] = (eventTypeCounts[category] ?? 0) + 1;
      if (isUSCoordinates(event.coordinates![1], event.coordinates![0]))
        usaCount.value += 1;
    }
  }

  // Phase 2: Balanced fill (zone + type constraints)
  for (const item of scored) {
    if (result.length >= maxResults) break;
    const event = item.event;
    const zone = longitudeZone(event.coordinates![0]);
    const category = event.categoryLabel;

    if (used.has(event.id)) continue;
    if ((zoneCounts[zone] ?? 0) >= Math.min(maxPerZone, MAX_EVENTS_PER_ZONE))
      continue;
    if ((eventTypeCounts[category] ?? 0) >= getTypeLimit(category)) continue;

    if (isUSCoordinates(event.coordinates![1], event.coordinates![0])) {
      if (usaCount.value >= MAX_USA_EVENTS) continue;
    }

    result.push(event);
    used.add(event.id);
    zoneCounts[zone] = (zoneCounts[zone] ?? 0) + 1;
    eventTypeCounts[category] = (eventTypeCounts[category] ?? 0) + 1;
    if (isUSCoordinates(event.coordinates![1], event.coordinates![0]))
      usaCount.value += 1;
  }

  // Phase 3: Ensure at least one entry from each major zone, if possible.
  for (const targetZone of zones) {
    if (result.length >= maxResults) break;
    if ((zoneCounts[targetZone] ?? 0) > 0) continue;

    const candidate = scored.find((item) => {
      const e = item.event;
      const cat = e.categoryLabel;
      if (used.has(e.id)) return false;
      if (longitudeZone(e.coordinates![0]) !== targetZone) return false;
      if ((eventTypeCounts[cat] ?? 0) >= getTypeLimit(cat)) return false;
      return true;
    });

    if (candidate) {
      const e = candidate.event;
      result.push(e);
      used.add(e.id);
      zoneCounts[targetZone] = (zoneCounts[targetZone] ?? 0) + 1;
      eventTypeCounts[e.categoryLabel] =
        (eventTypeCounts[e.categoryLabel] ?? 0) + 1;
      if (isUSCoordinates(e.coordinates![1], e.coordinates![0]))
        usaCount.value += 1;
    }
  }

  if (result.length > maxResults) return result.slice(0, maxResults);

  console.log(
    `[GlobalDiversity] Selected ${result.length} events from zones:`,
    zoneCounts,
    "typeCount:",
    eventTypeCounts,
    "usaCount:",
    usaCount.value,
  );

  return result;
}

/**
 * Fetch current active hazard events globally from multiple sources:
 * - NASA EONET (open events + recent closed events for better global coverage)
 * - GDACS (recent alerts with multiple proxy fallbacks)
 * - USGS (global significant earthquakes M4.5+)
 *
 * Enhanced with better global coverage and reliability improvements.
 * Designed for the homepage Global Disaster Tracker card.
 */
export const fetchGlobalActiveEvents =
  async (): Promise<GlobalHazardSummary> => {
    let isLive = false;
    const allEvents: NaturalEvent[] = [];

    // 1. NASA EONET — globally open events + recent closed events (last 120 days for better coverage)
    try {
      // Fetch both open and recent closed events for comprehensive global coverage
      const [openRes, recentRes] = await Promise.all([
        fetch(`${EONET_API}/events?status=open&limit=150&days=120`),
        fetch(`${EONET_API}/events?status=closed&limit=100&days=30`),
      ]);

      const allEonetEvents: NaturalEvent[] = [];

      if (openRes.ok) {
        const openData = await openRes.json();
        const openEvents = (openData.events ?? []).map(
          (event: any): NaturalEvent => {
            const category = event.categories?.[0];
            const geometry = event.geometry?.[0];
            const coords: [number, number] | undefined = geometry?.coordinates
              ? [geometry.coordinates[0], geometry.coordinates[1]]
              : undefined;
            const sourceUrl = event.sources?.[0]?.url;
            return {
              id: event.id,
              title: event.title,
              category: category?.id ?? "other",
              categoryLabel:
                EONET_CATEGORY_MAP[category?.id] ?? category?.title ?? "Other",
              status: "open",
              date: geometry?.date ?? "",
              magnitude: geometry?.magnitudeValue ?? undefined,
              magnitudeUnit: geometry?.magnitudeUnit ?? undefined,
              coordinates: coords,
              link: isUserFacingUrl(sourceUrl)
                ? sourceUrl
                : `https://eonet.gsfc.nasa.gov/api/v3/events/${event.id}`,
              source: "EONET",
            };
          },
        );
        allEonetEvents.push(...openEvents);
        console.log(`[GlobalHazard] EONET Open: ${openEvents.length} events`);
      }

      if (recentRes.ok) {
        const recentData = await recentRes.json();
        const recentEvents = (recentData.events ?? []).map(
          (event: any): NaturalEvent => {
            const category = event.categories?.[0];
            const geometry = event.geometry?.[0];
            const coords: [number, number] | undefined = geometry?.coordinates
              ? [geometry.coordinates[0], geometry.coordinates[1]]
              : undefined;
            const sourceUrl = event.sources?.[0]?.url;
            return {
              id: event.id + "_closed",
              title: event.title,
              category: category?.id ?? "other",
              categoryLabel:
                EONET_CATEGORY_MAP[category?.id] ?? category?.title ?? "Other",
              status: "closed",
              date: geometry?.date ?? "",
              closedDate: event.closed ?? undefined,
              magnitude: geometry?.magnitudeValue ?? undefined,
              magnitudeUnit: geometry?.magnitudeUnit ?? undefined,
              coordinates: coords,
              link: isUserFacingUrl(sourceUrl)
                ? sourceUrl
                : `https://eonet.gsfc.nasa.gov/api/v3/events/${event.id}`,
              source: "EONET",
            };
          },
        );
        allEonetEvents.push(...recentEvents);
        console.log(
          `[GlobalHazard] EONET Recent: ${recentEvents.length} events`,
        );
      }

      allEvents.push(...allEonetEvents);
      if (allEonetEvents.length > 0) isLive = true;
    } catch (err) {
      console.error("[GlobalHazard] EONET fetch failed:", err);
    }

    // 2. USGS — global significant earthquakes M4.5+ (last 30 days for active global coverage)
    try {
      const endTime = new Date().toISOString().split("T")[0];
      const startTime = new Date(Date.now() - 30 * 86_400_000)
        .toISOString()
        .split("T")[0];

      const params = new URLSearchParams({
        format: "geojson",
        starttime: startTime,
        endtime: endTime,
        minmagnitude: "4.5",
        orderby: "time-asc",
        limit: "200", // Increased limit for global coverage
      });

      const res = await fetch(`${USGS_API}?${params}`);
      if (res.ok) {
        const data = await res.json();
        const usgsEvents: NaturalEvent[] = (data.features ?? []).map(
          (feature: any): NaturalEvent => {
            const props = feature.properties;
            const coords = feature.geometry?.coordinates;
            return {
              id: feature.id,
              title: props.title,
              category: "earthquakes",
              categoryLabel: "Earthquake",
              status: "closed",
              date: new Date(props.time).toISOString(),
              magnitude: props.mag,
              magnitudeUnit: "M",
              coordinates: coords ? [coords[0], coords[1]] : undefined,
              link: props.url,
              source: "USGS",
            };
          },
        );
        allEvents.push(...usgsEvents);
        if (usgsEvents.length > 0) isLive = true;
        console.log(
          `[GlobalHazard] USGS: ${usgsEvents.length} global earthquakes`,
        );
      }
    } catch (err) {
      console.error("[GlobalHazard] USGS global fetch failed:", err);
    }

    // 3. GDACS — global alerts, last 60 days (with improved proxy fallback)
    try {
      const apiUrl = `${GDACS_API}?eventtypes=${GDACS_EVENT_TYPES}`;

      const res = await fetchWithCORSFallback(apiUrl);
      if (!res.ok) throw new Error(`GDACS responded ${res.status}`);
      const text = await res.text();
      let json: any;
      try {
        json = JSON.parse(text);
      } catch (parseErr) {
        throw new Error(`GDACS JSON parse failed: ${parseErr}`);
      }
      const items: any[] = json.results ?? json.features ?? [];

      const gdacsEvents: NaturalEvent[] = items.map(
        (item: any): NaturalEvent => {
          const p = item.properties ?? item;
          const typecode: string = p.eventtype ?? "";
          return {
            id: String(p.eventid ?? p.id ?? Math.random()),
            title:
              p.eventname ??
              p.name ??
              `${GDACS_TYPE_MAP[typecode] ?? typecode} Event`,
            category: typecode.toLowerCase(),
            categoryLabel: GDACS_TYPE_MAP[typecode] ?? typecode ?? "Other",
            status: "closed",
            date: p.fromdate ?? p.begindate ?? "",
            coordinates:
              p.latitude != null
                ? [Number(p.longitude), Number(p.latitude)]
                : undefined,
            link: p.link ?? "https://www.gdacs.org",
            source: "GDACS",
            alertLevel: p.alertlevel ?? undefined,
          };
        },
      );

      allEvents.push(...gdacsEvents);
      if (gdacsEvents.length > 0) isLive = true;
      console.log(`[GlobalHazard] GDACS: ${gdacsEvents.length} global alerts`);
    } catch (err) {
      console.error("[GlobalHazard] GDACS fetch failed (all proxies):", err);

      if (
        import.meta.env.VITE_SUPABASE_URL &&
        import.meta.env.VITE_SUPABASE_ANON_KEY
      ) {
        try {
          const edgeRes = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gdacs-disasters`,
            {
              headers: {
                apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
                Accept: "application/json",
              },
            },
          );

          const edgeBody = await edgeRes.json();
          if (!edgeRes.ok) {
            throw new Error(
              edgeBody.error ??
                `GDACS edge function returned ${edgeRes.status}`,
            );
          }

          const edgeItems: any[] = edgeBody.events ?? [];
          const edgeEvents: NaturalEvent[] = edgeItems.map(
            (item: any): NaturalEvent => {
              const typecode: string = item.eventtype ?? "";
              return {
                id: String(item.eventid ?? Math.random()),
                title:
                  item.eventname ??
                  item.name ??
                  `${GDACS_TYPE_MAP[typecode] ?? typecode} Event`,
                category: typecode.toLowerCase(),
                categoryLabel: GDACS_TYPE_MAP[typecode] ?? typecode ?? "Other",
                status: "closed",
                date: item.fromdate ?? item.todate ?? "",
                coordinates: undefined,
                link: item.url?.details ?? "https://www.gdacs.org",
                source: "GDACS",
                alertLevel: item.alertlevel ?? undefined,
              };
            },
          );

          allEvents.push(...edgeEvents);
          if (edgeEvents.length > 0) isLive = true;
          console.log(
            `[GlobalHazard] GDACS edge fallback: ${edgeEvents.length} events`,
          );
        } catch (edgeErr) {
          console.error("[GlobalHazard] GDACS edge fallback failed:", edgeErr);
        }
      }
    }

    // Deduplicate by id
    const seen = new Set<string>();
    const uniqueEvents = allEvents.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });

    const allOpenEvents = uniqueEvents.filter((e) => e.status === "open");
    const activeEvents = allOpenEvents.slice(0, 12); // Keep tracker count manageable and consistent
    const recentEvents = buildGlobalDiverseEvents(uniqueEvents, 12, 4); // Increased limits for better diversity

    // Highest GDACS alert level present in the full dataset
    let highestAlert: GlobalHazardSummary["highestAlert"] = null;
    uniqueEvents.forEach((e) => {
      if (e.alertLevel) {
        const lvl = e.alertLevel as keyof typeof ALERT_PRIORITY;
        if (
          highestAlert === null ||
          (ALERT_PRIORITY[lvl] ?? -1) > (ALERT_PRIORITY[highestAlert] ?? -1)
        ) {
          highestAlert = lvl as GlobalHazardSummary["highestAlert"];
        }
      }
    });

    console.log(
      `[GlobalHazard] Final: ${uniqueEvents.length} total events, ${allOpenEvents.length} active (api), ${activeEvents.length} shown, isLive=${isLive}`,
    );

    return {
      activeEvents,
      recentEvents,
      activeCount: activeEvents.length,
      highestAlert,
      fetchedAt: new Date().toISOString(),
      isLive,
    };
  };
